import { useEffect, useState } from 'react';
import { Banknote, CreditCard, MapPin, Phone, Printer, UserRound, Wallet } from 'lucide-react';
import Drawer from '../../components/Drawer';
import Button from '../../components/Button';
import AuthMessage from '../../components/AuthMessage';
import StatusBadge from '../../components/StatusBadge';
import {
  PAY_LATER_SETTLE_METHODS,
  PAY_LATER_STATUS,
  formatPayLaterAddress,
  mapPayLaterError,
  normalizePayLaterItem,
  settlePayLater,
} from '../../api/payLaters';
import { formatCurrency } from '../../data/mockData';
import form from '../../styles/form.module.css';
import styles from './PayLaterDrawer.module.css';

const METHOD_ICONS = {
  CASH: Banknote,
  UPI: Wallet,
  CARD: CreditCard,
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
}

export default function PayLaterDrawer({ record, token, onClose, onPaid, onReprint }) {
  const [method, setMethod] = useState('CASH');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!record) return;
    setMethod('CASH');
    setNote('');
    setError('');
  }, [record]);

  if (!record) return null;

  const customer = record.customer || {};
  const isPending = record.status === PAY_LATER_STATUS.PENDING;

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleMarkPaid = async (e) => {
    e?.preventDefault?.();
    if (!isPending || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const data = await settlePayLater(
        record.orderId || record.id,
        { payment_method: method, note: note.trim() || undefined },
        token,
      );
      const sale = data?.sale || data?.receipt || data;
      const updated = normalizePayLaterItem({
        ...record,
        ...sale,
        payment_status: 'COLLECTED',
        outstanding: 0,
        settlement_method: sale.settlement_method || method,
        settled_at: sale.settled_at || new Date().toISOString(),
      });
      onPaid?.({ ...data, paidVia: method, ...updated });
    } catch (err) {
      setError(mapPayLaterError(err));
      if (['already_settled', 'already_voided', 'not_pay_later', 'nothing_due'].includes(err?.code)) {
        onPaid?.(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      open={Boolean(record)}
      onClose={handleClose}
      title={record.displayId || record.id}
      subtitle={`${formatCurrency(isPending ? record.outstanding : record.total)} · ${customer.fullName || 'Customer'}`}
      footer={(
        <div className={styles.footerActions}>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Close
          </Button>
          <Button variant="outline" onClick={() => onReprint?.(record)} disabled={submitting}>
            <Printer size={14} /> Reprint
          </Button>
          {isPending && (
            <Button onClick={handleMarkPaid} disabled={submitting}>
              {submitting ? 'Saving…' : `Mark paid · ${method}`}
            </Button>
          )}
        </div>
      )}
    >
      <div className={styles.metaRow}>
        <StatusBadge status={record.status} size="sm" />
        <span className={styles.metaMuted}>{formatDate(record.createdAt)}</span>
      </div>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Customer</h3>
        <p className={styles.detailLine}>
          <UserRound size={14} /> {customer.fullName || '—'}
        </p>
        <p className={styles.detailLine}>
          <Phone size={14} /> {customer.phone ? `+91 ${customer.phone}` : '—'}
        </p>
        <p className={styles.detailLine}>
          <MapPin size={14} /> {formatPayLaterAddress(customer)}
        </p>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Sale</h3>
        <div className={styles.kv}>
          <span>{isPending ? 'Amount due' : 'Bill total'}</span>
          <strong>{formatCurrency(isPending ? record.outstanding : record.total)}</strong>
        </div>
        {record.counter && (
          <div className={styles.kv}>
            <span>Counter</span>
            <span>{record.counter}</span>
          </div>
        )}
        {record.status === PAY_LATER_STATUS.COLLECTED && (
          <>
            <div className={styles.kv}>
              <span>Paid via</span>
              <span>{record.paidVia || '—'}</span>
            </div>
            <div className={styles.kv}>
              <span>Paid at</span>
              <span>{formatDate(record.paidAt)}</span>
            </div>
            {record.settlementAmount != null && (
              <div className={styles.kv}>
                <span>Collected</span>
                <span>{formatCurrency(record.settlementAmount)}</span>
              </div>
            )}
            {record.settlementRoundOff != null && Number(record.settlementRoundOff) !== 0 && (
              <div className={styles.kv}>
                <span>Round off</span>
                <span>{formatCurrency(record.settlementRoundOff)}</span>
              </div>
            )}
          </>
        )}
      </section>

      {isPending && (
        <form className={styles.settleForm} onSubmit={handleMarkPaid}>
          <h3 className={styles.sectionTitle}>Collect full outstanding</h3>
          {error && <AuthMessage>{error}</AuthMessage>}
          <p className={styles.settleHint}>
            Collect {formatCurrency(record.outstanding)} — how did the customer pay?
          </p>
          <div className={styles.methodRow}>
            {PAY_LATER_SETTLE_METHODS.map(({ key, label }) => {
              const Icon = METHOD_ICONS[key] || Wallet;
              return (
                <button
                  key={key}
                  type="button"
                  className={`${styles.methodBtn} ${method === key ? styles.methodActive : ''}`}
                  onClick={() => setMethod(key)}
                  disabled={submitting}
                >
                  <Icon size={15} /> {label}
                </button>
              );
            })}
          </div>
          <label className={form.field}>
            <span className={form.label}>Note (optional)</span>
            <input
              className={form.input}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={200}
              disabled={submitting}
              placeholder="e.g. collected at counter"
            />
          </label>
        </form>
      )}
    </Drawer>
  );
}
